import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';

interface ProfilePicture {
  filename: string;
  url: string;
  size?: number;
  dimensions?: { width: number; height: number };
}

interface PaginationData {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export default function ProfilePictureOptimizer() {
  const api_url = useDefaultDataStore(state => state.active_school?.api_url);
  const [pictures, setPictures] = useState<ProfilePicture[]>([]);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [selectedPictures, setSelectedPictures] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 0,
  });
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [viewImage, setViewImage] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const toast = useRef<Toast>(null);

  // Helper function to get full image URL
  const getFullImageUrl = (url: string) => {
    if (!api_url) return url;
    // Remove leading slash if present
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    return `${api_url}${cleanUrl}`;
  };

  // Fetch profile pictures with pagination
  const fetchProfilePictures = async (page: number = 1, limit: number = 20) => {
  try {
    setLoading(true);
    const response = await fetch(`http://localhost/skoolpillar-erp/admin/profile-pictures?page=${page}&limit=${limit}`);
    const data = await response.json();

    if (data.status === 'success') {
      setPictures(data.data || []);
      setPagination(data.pagination);
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: data.message || 'Failed to fetch profile pictures',
        life: 3000,
      });
    }
  } catch (error) {
    console.error('Error fetching profile pictures:', error);
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load profile pictures',
      life: 3000,
    });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchProfilePictures(pagination.page, pagination.limit);
  }, []);

 const handlePageChange = (event: any) => {
  const newPage = event.page + 1;
  
  // Smooth scroll to top
  topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  
  fetchProfilePictures(newPage, event.rows);
};

  const toggleSelectPicture = (filename: string) => {
    const newSelected = new Set(selectedPictures);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedPictures(newSelected);
  };

  const selectAll = () => {
    const allFilenames = pictures.map(p => p.filename);
    setSelectedPictures(new Set(allFilenames));
  };

  const deselectAll = () => {
    setSelectedPictures(new Set());
  };

  const handleOptimize = async () => {
    if (selectedPictures.size === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select at least one picture to optimize',
        life: 3000,
      });
      return;
    }

    setShowOptimizeDialog(true);
    setOptimizing(true);
    setOptimizationProgress(0);

    try {
      const filenames = Array.from(selectedPictures);
      const total = filenames.length;
      let completed = 0;

      // Simulate optimization process (replace with actual API call)
      for (const filename of filenames) {
        // Replace with your actual optimization API endpoint
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulated delay
        
        completed++;
        setOptimizationProgress(Math.round((completed / total) * 100));
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Optimization Complete',
        detail: `${total} picture(s) optimized successfully`,
        life: 4000,
      });

      setSelectedPictures(new Set());
      fetchProfilePictures(pagination.page, pagination.limit);
    } catch (error) {
      console.error('Optimization error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Optimization Failed',
        detail: 'Failed to optimize selected pictures',
        life: 3000,
      });
    } finally {
      setOptimizing(false);
      setTimeout(() => {
        setShowOptimizeDialog(false);
        setOptimizationProgress(0);
      }, 1000);
    }
  };

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      // Replace with your actual delete API endpoint
      toast.current?.show({
        severity: 'info',
        summary: 'Delete',
        detail: `Delete functionality for ${filename} will be implemented`,
        life: 3000,
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Delete Failed',
        detail: 'Failed to delete picture',
        life: 3000,
      });
    }
  };

  if (loading && pictures.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fdfdfdff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }}></i>
          <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>Loading profile pictures...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={topRef} style={{
      minHeight: '100vh',
      padding: '0px',
      background: '#fdfdfdff',
    }}>
      <Toast ref={toast} />

      {/* Animations */}
      <style>{`
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .image-card {
          transition: all 0.3s ease;
        }

        .image-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
        }

        .checkbox-custom {
          width: 20px;
          height: 20px;
          cursor: pointer;
          accent-color: #3b82f6;
        }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '24px',
        padding: '32px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="pi pi-images" style={{ fontSize: '18px', color: 'white' }}></i>
          </div>
          <div>
            <h1 style={{
              color: 'white',
              fontSize: '28px',
              fontWeight: '600',
              margin: '0 0 4px 0',
            }}>
              Profile Pictures Optimizer
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                background: 'rgba(74, 222, 128, 0.9)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
              }}>
                {pagination.total} Pictures
              </span>
              {selectedPictures.size > 0 && (
                <span style={{
                  background: 'rgba(251, 191, 36, 0.9)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                }}>
                  {selectedPictures.size} Selected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        border: '2px solid #dbeafe',
        animation: 'fadeInSlide 0.5s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: '10px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            height: '32px',
          }}>
            <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }}></i>
          </div>
          <div>
            <h4 style={{
              color: '#2855ae',
              fontSize: '14px',
              fontWeight: '600',
              margin: '0 0 6px 0',
            }}>
              Optimization Information
            </h4>
            <p style={{
              color: '#6b7280',
              fontSize: '13px',
              margin: 0,
              lineHeight: '1.6',
            }}>
              Select profile pictures to optimize for better performance. Optimization reduces file size while maintaining quality.
              Use batch selection for efficient processing.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {pictures.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <button
            onClick={selectAll}
            disabled={pictures.length === 0}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 16px',
              cursor: pictures.length === 0 ? 'not-allowed' : 'pointer',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: pictures.length === 0 ? 0.5 : 1,
            }}
          >
            <i className="pi pi-check-square" style={{ fontSize: '14px' }}></i>
            Select All
          </button>

          <button
            onClick={deselectAll}
            disabled={selectedPictures.size === 0}
            style={{
              background: '#6b7280',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 16px',
              cursor: selectedPictures.size === 0 ? 'not-allowed' : 'pointer',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: selectedPictures.size === 0 ? 0.5 : 1,
            }}
          >
            <i className="pi pi-times" style={{ fontSize: '14px' }}></i>
            Deselect All
          </button>

          <button
            onClick={handleOptimize}
            disabled={selectedPictures.size === 0 || optimizing}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 16px',
              cursor: selectedPictures.size === 0 || optimizing ? 'not-allowed' : 'pointer',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: selectedPictures.size === 0 || optimizing ? 0.5 : 1,
              marginLeft: 'auto',
            }}
          >
            <i className={optimizing ? "pi pi-spin pi-spinner" : "pi pi-bolt"} style={{ fontSize: '14px' }}></i>
            {optimizing ? 'Optimizing...' : `Optimize ${selectedPictures.size > 0 ? `(${selectedPictures.size})` : ''}`}
          </button>
        </div>
      )}

      {/* Pictures Grid */}
      {pictures.length > 0 ? (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '24px',
          }}>
            {pictures.map((picture, index) => (
              <div
                key={picture.filename}
                className="image-card"
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '16px',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  border: selectedPictures.has(picture.filename) ? '2px solid #3b82f6' : '2px solid #f1f5f9',
                  animation: `fadeInSlide 0.5s ease-out ${index * 0.05}s backwards`,
                }}
              >
                {/* Checkbox */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  zIndex: 10,
                }}>
                  <input
                    type="checkbox"
                    className="checkbox-custom"
                    checked={selectedPictures.has(picture.filename)}
                    onChange={() => toggleSelectPicture(picture.filename)}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  display: 'flex',
                  gap: '6px',
                  zIndex: 10,
                }}>
                  <button
                    onClick={() => setViewImage(getFullImageUrl(picture.url))}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      width: '28px',
                      height: '28px',
                      cursor: 'pointer',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className="pi pi-eye" style={{ fontSize: '12px' }}></i>
                  </button>

                  <button
                    onClick={() => handleDelete(picture.filename)}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '8px',
                      width: '28px',
                      height: '28px',
                      cursor: 'pointer',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className="pi pi-trash" style={{ fontSize: '12px' }}></i>
                  </button>
                </div>

                {/* Image Preview */}
                <div style={{
                  width: '100%',
                  height: '200px',
                  background: '#f9fafb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <img
                    src={getFullImageUrl(picture.url)}
                    alt={picture.filename}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>

                {/* Filename */}
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#2855ae',
                  marginBottom: '4px',
                  wordBreak: 'break-all',
                }}>
                  {picture.filename}
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#6b7280',
                }}>
                  {picture.size && (
                    <span>
                      <i className="pi pi-file" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                      {(picture.size / 1024).toFixed(1)} KB
                    </span>
                  )}
                  {picture.dimensions && (
                    <span>
                      <i className="pi pi-arrows-alt" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                      {picture.dimensions.width}×{picture.dimensions.height}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '16px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          }}>
            <Paginator
              first={(pagination.page - 1) * pagination.limit}
              rows={pagination.limit}
              totalRecords={pagination.total}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      ) : (
        /* Empty State */
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          animation: 'fadeInSlide 0.5s ease-out',
        }}>
          <i className="pi pi-images" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
          <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>
            No Profile Pictures Found
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
            There are no profile pictures to display at the moment.
          </p>
        </div>
      )}

      {/* Optimization Progress Dialog */}
      <Dialog
        header="Optimizing Pictures"
        visible={showOptimizeDialog}
        style={{ width: '450px' }}
        onHide={() => !optimizing && setShowOptimizeDialog(false)}
        closable={!optimizing}
      >
        <div style={{ padding: '20px 0' }}>
          <ProgressBar value={optimizationProgress} />
          <p style={{
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px',
            marginTop: '16px',
          }}>
            {optimizing ? `Processing... ${optimizationProgress}%` : 'Complete!'}
          </p>
        </div>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        header="Image Preview"
        visible={viewImage !== null}
        style={{ width: '90vw', maxWidth: '800px' }}
        onHide={() => setViewImage(null)}
      >
        {viewImage && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
          }}>
            <img
              src={viewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
}